import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { formatLiveInput, formatWithThousandsSeparator } from '@/utils/format';

const NUMERIC_PATTERN = /^[\d]*\.?[\d]*$/;

interface UseFormattedNumericInputOptions {
  fieldName: string;
  setFieldValue: (field: string, value: number | string | undefined) => void;
  currentValue: number | string | null | undefined;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export function useFormattedNumericInput({
  fieldName,
  setFieldValue,
  currentValue,
  onBlur,
}: UseFormattedNumericInputOptions) {
  const [localValue, setLocalValue] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorRef = useRef<number | null>(null);
  const committedRef = useRef<number | string | null | undefined>(currentValue);

  // Restore cursor position after React re-renders the formatted value.
  // Intentionally has no dependency array — cursorRef is a ref, not reactive state.
  useLayoutEffect(() => {
    if (cursorRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorRef.current, cursorRef.current);
      cursorRef.current = null;
    }
  });

  // Sync local editing buffer when Formik value changes externally (e.g., resetForm)
  useEffect(() => {
    if (localValue !== null && currentValue !== committedRef.current) {
      setLocalValue(formatLiveInput(String(currentValue ?? '')));
    }
    committedRef.current = currentValue;
  }, [currentValue, localValue]);

  const displayValue = localValue !== null ? localValue : formatWithThousandsSeparator(currentValue ?? undefined);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      if (inputValue === '') {
        setLocalValue('');
        setFieldValue(fieldName, '');
        return;
      }

      // Strip commas and common paste artifacts (currency symbols, spaces)
      const cleanedValue = inputValue.replace(/[,\s$€£]/g, '');

      if (!NUMERIC_PATTERN.test(cleanedValue)) {
        return;
      }

      // Compute cursor position accounting for comma changes
      const rawCursor = e.target.selectionStart ?? inputValue.length;
      const formatted = formatLiveInput(cleanedValue);
      const commasBefore = (inputValue.slice(0, rawCursor).match(/,/g) || []).length;
      const digitPosition = rawCursor - commasBefore;
      let newCursor = 0;
      let digitsSeen = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (formatted[i] !== ',') digitsSeen++;
        newCursor = i + 1;
        if (digitsSeen === digitPosition) break;
      }
      cursorRef.current = newCursor;

      setLocalValue(formatted);

      // Commit parsed numeric value to Formik; clear on unparseable partial input
      const num = parseFloat(cleanedValue);
      if (!isNaN(num)) {
        setFieldValue(fieldName, num);
      } else {
        setFieldValue(fieldName, '');
      }
    },
    [fieldName, setFieldValue],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setLocalValue(null);
      onBlur?.(e);
    },
    [onBlur],
  );

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
    e.target.select();
  }, []);

  return { displayValue, handleChange, handleBlur, handleFocus, inputRef };
}
